import styled from "styled-components";
import TextButtonWithArrow from "../../components/atoms/TextButtonWithArrow";
import {
  offsetInstructionsText,
  removalInstructionsText,
  initialDrawingMessages,
  subsequentPolygonDrawingMessage,
  overlappingPolygonMessage,
  replaceFeatureTypeToken,
  overlappingPolygonHeading,
  minimumAreaToPerimeterRatio,
} from "../../lib/constants";
import DrawOption from "../../components/molecules/DrawOption";
import { useCallback, useEffect, useRef, useState } from "react";
import colours from "../../lib/colours";
import Callout from "../../components/molecules/Callout";
import {
  useDialogContext,
  useMapDialogContext,
  useDrawingContext,
  useFeaturesContext,
  useFeaturesDispatchContext,
  useMapMessagingContext,
  useMapScaleContext,
  useMapModeContext,
  useMapContext,
  useNvrToolModeContext,
} from "../../context/ContextProvider";
import {
  featureTypes,
  featureSource,
  nvrTools,
  Patch,
  mapAdditionalModes,
  mapModes,
  RevegetationArea,
  nvrToolModes,
  featureTypeFieldValues,
  validations,
  validationFeatureCreationContext,
} from "../../lib/types";
import { IState } from "../../context/featuresReducer";
import {
  getArea,
  getAreaToPerimeterRatio,
  getOverlappingFeatures,
  isPolygonInvalid,
  mergePolygons,
  polygonHasAtLeastOneDrawnVertex,
} from "../../utils/featureUtils";
import Polygon from "esri/geometry/Polygon";
import Point from "esri/geometry/Point";
import SketchViewModel from "esri/widgets/Sketch/SketchViewModel";
import GraphicsLayer from "esri/layers/GraphicsLayer";
import mapConfig from "../../lib/mapConfig";
import Graphic from "@arcgis/core/Graphic";
import { IProps as DialogProps } from "../../components/organisms/Dialog";
import { initialDialogProps } from "./DialogContainer";
import { initialDialogProps as initialMapDialogProps } from "./DialogMapContainer";
import DialogCallout from "../../components/other/DialogCallout";
import TreeProperties from "./TreeProperties";
import HowToMapRemoval from "./HowToMapRemoval";
import { replaceStringTokens, splitCamelCase } from "../../utils/otherUtils";
import HowToMapOffset from "./HowToMapOffset";
import HowToRevegetation from "./HowToRevegetation";
import { runValidations } from "../../utils/validations";
import validationMessages from "../../lib/simpleValidationMessages";

const Container = styled.div`
  margin-bottom: 32px;
`;

const InstructionsContainer = styled.div`
  display: flex;
  align-items: center;
  height: 34px;
  padding-inline: 12px;
`;

const DrawOptionsContainer = styled.div``;

const Separator = styled.div`
  height: 10px;
  margin-inline: 12px;
  border-bottom: 1px solid ${colours.th1l4};
  margin-bottom: 10px;
`;

const CalloutContainer = styled.div`
  margin-block: 8px;
`;

const drawOptionToSketchTool: Record<featureTypes, "polygon" | "point"> = {
  [featureTypes.patch]: "polygon",
  [featureTypes.tree]: "point",
  [featureTypes.revegetationArea]: "polygon",
};

interface IProps {
  drawnGraphicRef: any;
}

function DrawWidget({ drawnGraphicRef }: IProps) {
  const state: IState = useFeaturesContext();
  const dispatch = useFeaturesDispatchContext();
  const { nvrToolMode }: { nvrToolMode: nvrToolModes } =
    useNvrToolModeContext();
  const { map } = useMapContext();
  const {
    sketchVm,
    pointGraphicsLayer,
  }: {
    sketchVm: SketchViewModel;
    pointGraphicsLayer: GraphicsLayer;
  } = useDrawingContext();
  const {
    mapMode,
    setMapMode,
  }: {
    mapMode: mapModes;
    setMapMode: any;
  } = useMapModeContext();
  const { setDialogProps }: { setDialogProps: (props: DialogProps) => void } =
    useDialogContext();
  const {
    setMapDialogProps,
  }: { setMapDialogProps: (props: DialogProps) => void } =
    useMapDialogContext();
  const { setMapMessage, setMapMessageType } = useMapMessagingContext();
  const { mapScale } = useMapScaleContext();

  const [showRevegetationWarning, setShowRevegetationWarning] = useState(true);

  const createEventListener = useRef<any>();
  const drawEventListener = useRef<any>();
  const undoEventListener = useRef<any>();
  const mapModeRef = useRef<mapModes | undefined>();

  const onHelpClick = () => {
    const title =
      nvrToolMode === nvrTools.removal
        ? "Mapping native vegetation removal in NVR Map"
        : "Mapping native vegetation offsets in NVR Map";
    const helpContent =
      nvrToolMode === nvrTools.removal ? (
        <HowToMapRemoval />
      ) : (
        <HowToMapOffset />
      );

    setDialogProps({
      open: true,
      onClose: () => {
        setDialogProps(initialDialogProps);
      },
      heading: title,
      children: helpContent,
      modal: false,
    });
  };

  const onRevegetationInfoClick = () => {
    const title = "Revegetation area requirements";
    const helpContent = <HowToRevegetation />;

    setDialogProps({
      open: true,
      onClose: () => {
        setDialogProps(initialDialogProps);
      },
      heading: title,
      children: helpContent,
      modal: false,
    });
  };

  const resumeDrawing = useCallback(() => {
    if (mapMode) {
      setDialogProps(initialDialogProps);
      setMapDialogProps(initialMapDialogProps);
      setMapMessage(initialDrawingMessages[mapMode as featureTypes]);
      setTimeout(
        () => sketchVm.create(drawOptionToSketchTool[mapMode as featureTypes]),
        200
      );
    }
  }, [mapMode, setDialogProps, setMapDialogProps, setMapMessage, sketchVm]);

  const addPatch = useCallback(
    async (graphic: Graphic) => {
      const feature: any = {
        source: featureSource.Drawn,
        geom: graphic.geometry.toJSON(),
        area: await getArea(graphic.geometry as Polygon),
        type: featureTypeFieldValues.patch,
      };
      dispatch({
        type: "addPatch",
        payload: feature,
      });
      drawnGraphicRef.current = undefined;
    },
    [dispatch, drawnGraphicRef]
  );

  const addRevegetationArea = useCallback(
    async (graphic: Graphic, apRatio: number) => {
      const feature: any = {
        source: featureSource.Drawn,
        geom: graphic.geometry.toJSON(),
        area: await getArea(graphic.geometry as Polygon),
        apRatio,
        type: featureTypeFieldValues.revegetationArea,
      };
      dispatch({
        type: "addRevegetationArea",
        payload: feature,
      });
      drawnGraphicRef.current = undefined;
    },
    [dispatch, drawnGraphicRef]
  );

  const onDiscard = useCallback(
    (graphic: Graphic) => {
      pointGraphicsLayer.remove(graphic);
      drawnGraphicRef.current = undefined;
      resumeDrawing();
    },
    [pointGraphicsLayer, resumeDrawing, drawnGraphicRef]
  );

  const displayTreeDialog = useCallback(
    (graphic: any) => {
      setMapMessage(undefined);
      setMapDialogProps({
        open: true,
        onClose: () => onDiscard(graphic),
        modal: false,
        transparentOverlay: true,
        showHeader: false,
        showFooter: false,
        children: (
          <TreeProperties
            graphic={graphic}
            onClose={() => onDiscard(graphic)}
            onSubmit={() => {
              drawnGraphicRef.current = undefined;
              resumeDrawing();
            }}
          />
        ),
      });
    },
    [
      setMapMessage,
      setMapDialogProps,
      onDiscard,
      resumeDrawing,
      drawnGraphicRef,
    ]
  );

  const handleCriticalValidationFailure = useCallback(
    (graphic: Graphic, messageContent: any, messageTitle: string) => {
      setDialogProps({
        open: true,
        onClose: () => onDiscard(graphic),
        heading: messageTitle,
        children: (
          <DialogCallout
            calloutConfigs={[
              {
                type: "warning",
                content: messageContent,
              },
            ]}
          ></DialogCallout>
        ),
      });
    },
    [setDialogProps, onDiscard]
  );

  const handleNonCriticalValidationFailure = useCallback(
    (
      graphic: Graphic,
      featureType: featureTypes,
      messages: any[],
      headings?: Array<string | undefined>
    ) => {
      const heading =
        headings && headings.length === 1 && headings[0]
          ? headings[0]
          : "Warning";
      const calloutConfigs = messages.map((x) => ({
        type: "info",
        content: x,
      }));
      setDialogProps({
        open: true,
        onClose: () => {
          setDialogProps(initialDialogProps);
          if (featureType === featureTypes.tree) {
            displayTreeDialog(graphic);
          }
        },
        heading: calloutConfigs.length === 1 ? heading : "Warnings",
        children: (
          <DialogCallout calloutConfigs={calloutConfigs}></DialogCallout>
        ),
      });
    },
    [setDialogProps, displayTreeDialog]
  );

  const onMerge = useCallback(
    async (featureType: string, polygonIds: string[], newGeom: Polygon) => {
      let dispatchType;
      if (featureType === featureTypes.patch) {
        dispatchType = "mergePatches";
      } else if (featureType === featureTypes.revegetationArea) {
        dispatchType = "mergeRevegetationAreas";
      } else {
        throw new Error(
          `Unsupported featureType value ${featureType} when attempting to merge polygons`
        );
      }
      const mergedFeature: any = {
        source: featureSource.Drawn,
        geom: newGeom.toJSON(),
        area: await getArea(newGeom as Polygon),
      };
      if (featureType === featureTypes.revegetationArea) {
        mergedFeature.apRatio = await getAreaToPerimeterRatio(
          newGeom as Polygon
        );
      }
      dispatch({
        type: dispatchType,
        payload: {
          polygonIds: polygonIds,
          mergedFeature: mergedFeature,
        },
      });
      drawnGraphicRef.current = undefined;
      resumeDrawing();
      if (
        mergedFeature.apRatio !== undefined &&
        mergedFeature.apRatio < minimumAreaToPerimeterRatio
      ) {
        handleNonCriticalValidationFailure(
          mergedFeature,
          featureTypes.revegetationArea,
          [validationMessages.apRatio.content()],
          [validationMessages.apRatio.title]
        );
      }
    },
    [
      dispatch,
      resumeDrawing,
      drawnGraphicRef,
      handleNonCriticalValidationFailure,
    ]
  );

  const getNonCriticalPatchInfo = useCallback(
    async (graphic: Graphic) => {
      const polygon = graphic.geometry as Polygon;
      const validationsToRun = [];
      if (nvrToolMode === nvrTools.removal) {
        validationsToRun.push(validations.msaConservation);
        validationsToRun.push(validations.msa12Precincts);
      }
      if (nvrToolMode === nvrTools.offset) {
        validationsToRun.push(validations.wetlands);
        validationsToRun.push(validations.parcelBoundaryProximity);
      }
      const nonCriticalValidations = await runValidations(
        validationsToRun,
        polygon,
        featureTypes.patch,
        map,
        state
      );
      return nonCriticalValidations.filter((x) => x.fail);
    },
    [nvrToolMode, map, state]
  );

  const getNonCriticalTreeInfo = useCallback(
    async (graphic: Graphic) => {
      const point = graphic.geometry as Point;
      const validationsToRun = [];
      if (nvrToolMode === nvrTools.removal) {
        validationsToRun.push(validations.msaConservation);
        validationsToRun.push(validations.msa12Precincts);
      }
      if (nvrToolMode === nvrTools.offset) {
        validationsToRun.push(validations.parcelBoundaryProximity);
      }
      const nonCriticalValidations = await runValidations(
        validationsToRun,
        point,
        featureTypes.tree,
        map,
        state
      );
      return nonCriticalValidations.filter((x) => x.fail);
    },
    [nvrToolMode, map, state]
  );

  const getNonCriticalRevegetationInfo = useCallback(
    async (graphic: Graphic, apRatio?: number) => {
      const polygon = graphic.geometry as Polygon;
      const validationsToRun = [];
      if (nvrToolMode === nvrTools.offset) {
        validationsToRun.push(validations.parcelBoundaryProximity);
      }
      if (apRatio) {
        validationsToRun.push(validations.apRatio);
      }
      const nonCriticalValidations = await runValidations(
        validationsToRun,
        polygon,
        featureTypes.revegetationArea,
        map,
        state,
        undefined,
        { apRatio }
      );
      return nonCriticalValidations.filter((x) => x.fail);
    },
    [nvrToolMode, map, state]
  );

  const handleInvalidPolygonAfterMerge = useCallback(
    (graphic: Graphic, featureType: featureTypes) => {
      setMapMessage(undefined);
      handleCriticalValidationFailure(
        graphic,
        validationMessages.invalidPolygon.getInvalidAfterMergeMessage(
          featureType
        ),
        validationMessages.invalidPolygon.getMessageTitle(featureType)
      );
    },
    [setMapMessage, handleCriticalValidationFailure]
  );

  const handleValidOverlap = useCallback(
    async (
      graphic: Graphic,
      featureType: string,
      overlappingPolygonIds: string[],
      newGeom: Polygon
    ) => {
      const pluralSuffix =
        featureType === featureTypes.revegetationArea ? "s" : "es";
      const messageEnd =
        overlappingPolygonIds.length === 1
          ? ` ${overlappingPolygonIds[0]}.`
          : `${pluralSuffix} ${overlappingPolygonIds.join(", ")}.`;

      let calloutConfigs: any[] = [
        {
          type: "warning",
          content:
            replaceStringTokens(
              overlappingPolygonMessage,
              replaceFeatureTypeToken,
              splitCamelCase(featureType)
            ) + messageEnd,
        },
      ];

      if (featureType === featureTypes.patch) {
        const info = await getNonCriticalPatchInfo(graphic);
        calloutConfigs = calloutConfigs.concat(
          info.map((x) => ({
            type: "info",
            content: x.messageContent,
          }))
        );
      }

      if (featureType === featureTypes.revegetationArea) {
        const info = await getNonCriticalRevegetationInfo(graphic);
        calloutConfigs = calloutConfigs.concat(
          info.map((x) => ({
            type: "info",
            content: x.messageContent,
          }))
        );
      }

      setDialogProps({
        open: true,
        onClose: () => onDiscard(graphic),
        heading: replaceStringTokens(
          overlappingPolygonHeading,
          replaceFeatureTypeToken,
          splitCamelCase(featureType)
        ),
        children: (
          <DialogCallout calloutConfigs={calloutConfigs}></DialogCallout>
        ),
        additionalButtons: [
          {
            text: `Merge overlapping ${splitCamelCase(
              featureType
            )}${pluralSuffix}`,
            onClickHandler: () =>
              onMerge(featureType, overlappingPolygonIds, newGeom),
          },
        ],
        closeButtonText: `Discard last drawn ${splitCamelCase(featureType)}`,
        showXButton: false,
      });
    },
    [
      setDialogProps,
      getNonCriticalPatchInfo,
      getNonCriticalRevegetationInfo,
      onDiscard,
      onMerge,
    ]
  );

  const handleOverlappingPolygons = useCallback(
    (
      graphic: Graphic,
      featureType: featureTypes,
      overlappingPolygonIds: string[]
    ) => {
      setMapMessage(undefined);
      const toMerge = [graphic.geometry as Polygon].concat(
        overlappingPolygonIds.map((x) => state.features[x].geom as Polygon)
      );
      const newGeom = mergePolygons(toMerge) as Polygon;
      if (isPolygonInvalid(newGeom)) {
        handleInvalidPolygonAfterMerge(graphic, featureType);
      } else {
        handleValidOverlap(
          graphic,
          featureType,
          overlappingPolygonIds,
          newGeom
        );
      }
    },
    [state, setMapMessage, handleInvalidPolygonAfterMerge, handleValidOverlap]
  );

  const handleDrawnPatch = useCallback(
    async (graphic: Graphic) => {
      const polygon = graphic.geometry as Polygon;
      let supportingValidationData = {};
      const validationsToRun: validations[] = [
        validations.invalidPolygon,
        validations.msaLevyArea,
        validations.outsideSupportingRasters,
      ];
      if (nvrToolMode === nvrTools.offset) {
        validationsToRun.push(validations.overlappingTypes);
        validationsToRun.push(validations.treesBelowDbhThreshold);
        supportingValidationData = {
          otherFeatureType: featureTypes.revegetationArea,
          otherGeoms: state.revegetationAreasList.map(
            (x: string) => state.features[x].geom
          ),
        };
      }
      const criticalValidations = await runValidations(
        validationsToRun,
        polygon,
        featureTypes.patch,
        map,
        state,
        validationFeatureCreationContext.draw,
        { nvrToolMode, ...supportingValidationData }
      );
      const failedCriticalValidations = criticalValidations.filter(
        (x) => x.fail
      );
      if (failedCriticalValidations.length) {
        const { messageContent, messageTitle } = failedCriticalValidations[0];
        handleCriticalValidationFailure(
          graphic,
          messageContent,
          messageTitle || "Warning"
        );
        return;
      }

      const currentPatches = state.patchesList.map(
        (x: string) => state.features[x]
      ) as Patch[];

      const overlappingPatches = getOverlappingFeatures(
        polygon,
        currentPatches
      );
      if (overlappingPatches.length) {
        handleOverlappingPolygons(
          graphic,
          featureTypes.patch,
          overlappingPatches
        );
      } else {
        addPatch(graphic);
        resumeDrawing();
        const info = await getNonCriticalPatchInfo(graphic);
        if (info.length) {
          handleNonCriticalValidationFailure(
            graphic,
            featureTypes.patch,
            info.map((x) => x.messageContent),
            info.map((x) => x.messageTitle)
          );
        }
      }
    },
    [
      addPatch,
      handleOverlappingPolygons,
      handleCriticalValidationFailure,
      handleNonCriticalValidationFailure,
      getNonCriticalPatchInfo,
      nvrToolMode,
      resumeDrawing,
      state,
      map,
    ]
  );

  const handleDrawnTree = useCallback(
    async (graphic: Graphic) => {
      const point = graphic.geometry as Point;

      const validationsToRun: validations[] = [
        validations.msaLevyArea,
        validations.outsideSupportingRasters,
      ];
      const criticalValidations = await runValidations(
        validationsToRun,
        point,
        featureTypes.tree,
        map,
        state,
        validationFeatureCreationContext.draw,
        { nvrToolMode }
      );
      const failedCriticalValidations = criticalValidations.filter(
        (x) => x.fail
      );
      if (failedCriticalValidations.length) {
        const { messageContent, messageTitle } = failedCriticalValidations[0];
        handleCriticalValidationFailure(
          graphic,
          messageContent,
          messageTitle || "Warning"
        );
        return;
      }

      const info = await getNonCriticalTreeInfo(graphic);
      if (info.length) {
        handleNonCriticalValidationFailure(
          graphic,
          featureTypes.tree,
          info.map((x) => x.messageContent),
          info.map((x) => x.messageTitle)
        );
      } else {
        displayTreeDialog(graphic);
      }
    },
    [
      handleCriticalValidationFailure,
      handleNonCriticalValidationFailure,
      getNonCriticalTreeInfo,
      displayTreeDialog,
      map,
      state,
      nvrToolMode,
    ]
  );

  const handleDrawnRevegetationArea = useCallback(
    async (graphic: Graphic) => {
      const polygon = graphic.geometry as Polygon;
      let supportingValidationData = {};
      const validationsToRun: validations[] = [
        validations.invalidPolygon,
        validations.msaLevyArea,
        validations.outsideSupportingRasters,
      ];
      if (nvrToolMode === nvrTools.offset) {
        validationsToRun.push(validations.overlappingTypes);
        validationsToRun.push(validations.wetlands);
        validationsToRun.push(validations.nonWoodyEvc);
        supportingValidationData = {
          otherFeatureType: featureTypes.patch,
          otherGeoms: state.patchesList.map(
            (x: string) => state.features[x].geom
          ),
        };
      }
      const criticalValidations = await runValidations(
        validationsToRun,
        polygon,
        featureTypes.revegetationArea,
        map,
        state,
        validationFeatureCreationContext.draw,
        { nvrToolMode, ...supportingValidationData }
      );
      const failedCriticalValidations = criticalValidations.filter(
        (x) => x.fail
      );
      if (failedCriticalValidations.length) {
        const { messageContent, messageTitle } = failedCriticalValidations[0];
        handleCriticalValidationFailure(
          graphic,
          messageContent,
          messageTitle || "Warning"
        );
        return;
      }

      const currentRevegetationAreas = state.revegetationAreasList.map(
        (x: string) => state.features[x]
      ) as RevegetationArea[];

      const overlappingRevegetationAreas = getOverlappingFeatures(
        polygon,
        currentRevegetationAreas
      );
      if (overlappingRevegetationAreas.length) {
        handleOverlappingPolygons(
          graphic,
          featureTypes.revegetationArea,
          overlappingRevegetationAreas
        );
      } else {
        const apRatio = await getAreaToPerimeterRatio(
          graphic.geometry as Polygon
        );
        addRevegetationArea(graphic, apRatio);
        resumeDrawing();
        const info = await getNonCriticalRevegetationInfo(graphic, apRatio);
        if (info.length) {
          handleNonCriticalValidationFailure(
            graphic,
            featureTypes.revegetationArea,
            info.map((x) => x.messageContent),
            info.map((x) => x.messageTitle)
          );
        }
      }
    },
    [
      addRevegetationArea,
      handleOverlappingPolygons,
      handleCriticalValidationFailure,
      handleNonCriticalValidationFailure,
      getNonCriticalRevegetationInfo,
      resumeDrawing,
      nvrToolMode,
      state,
      map,
    ]
  );

  const cleanUp = useCallback(() => {
    if (sketchVm) {
      sketchVm.cancel();
    }
    if (drawnGraphicRef.current) {
      pointGraphicsLayer.remove(drawnGraphicRef.current);
      drawnGraphicRef.current = undefined;
    }
    if (
      Object.keys(featureTypes).includes(mapModeRef.current as featureTypes)
    ) {
      setMapMessage(undefined);
    }
    setMapDialogProps(initialMapDialogProps);
  }, [
    sketchVm,
    mapModeRef,
    pointGraphicsLayer,
    setMapMessage,
    setMapDialogProps,
    drawnGraphicRef,
  ]);

  // feature create events
  useEffect(() => {
    if (!sketchVm) return;
    if (createEventListener.current) {
      createEventListener.current.remove();
    }
    createEventListener.current = sketchVm.on(
      "create",
      ({ graphic, state: eventState }: { graphic: Graphic; state: string }) => {
        if (eventState === "complete" && mapMode !== undefined) {
          drawnGraphicRef.current = graphic;
          resumeDrawing();
          if (mapMode === featureTypes.patch) {
            handleDrawnPatch(graphic);
          }
          if (mapMode === featureTypes.tree) {
            handleDrawnTree(graphic);
          }
          if (mapMode === featureTypes.revegetationArea) {
            handleDrawnRevegetationArea(graphic);
          }
        }
      }
    );
    return () => {
      if (createEventListener.current) createEventListener.current.remove();
    };
  }, [
    sketchVm,
    mapMode,
    state,
    dispatch,
    setMapMessage,
    handleDrawnPatch,
    handleDrawnTree,
    handleDrawnRevegetationArea,
    resumeDrawing,
    drawnGraphicRef,
  ]);

  // Enable/Disable drawing when not in scale and set map messages
  useEffect(() => {
    if (!Object.keys(featureTypes).includes(mapMode) || !sketchVm) return;
    if (mapScale > mapConfig.minDrawingScale) {
      setMapMessage("Zoom in to draw");
      setMapMessageType("warning");
      sketchVm.cancel();
    } else if (
      sketchVm.state === "ready" &&
      mapScale <= mapConfig.minDrawingScale
    ) {
      setMapMessage(initialDrawingMessages[mapMode as featureTypes]);
      setMapMessageType("action");
      setTimeout(
        () => sketchVm.create(drawOptionToSketchTool[mapMode as featureTypes]),
        200
      );

      if (drawEventListener.current) {
        drawEventListener.current.remove();
      }

      if (undoEventListener.current) {
        undoEventListener.current.remove();
      }
      drawEventListener.current = sketchVm.on(
        "create",
        ({ state: eventState }: { state: string }) => {
          if (mapMode === featureTypes.tree) return;
          const polygon = sketchVm.createGraphic?.geometry as Polygon;
          if (polygon && !polygonHasAtLeastOneDrawnVertex(polygon)) {
            setMapMessage(subsequentPolygonDrawingMessage);
          }
        }
      );
      undoEventListener.current = sketchVm.on(
        "undo",
        ({ graphics }: { graphics: Graphic[] }) => {
          const polygon = graphics[0].geometry as Polygon;
          if (polygon && !polygonHasAtLeastOneDrawnVertex(polygon)) {
            setMapMessage(initialDrawingMessages[mapMode as featureTypes]);
          }
        }
      );
    }
    return () => {
      if (drawEventListener.current) drawEventListener.current.remove();
      if (undoEventListener.current) undoEventListener.current.remove();
    };
  }, [sketchVm, mapMode, mapScale, setMapMessage, setMapMessageType]);

  // add back temporary drawn graphic if there has been a state refresh
  // as state refresh will clean up graphicslayer
  useEffect(() => {
    if (drawnGraphicRef.current) {
      setTimeout(() => {
        pointGraphicsLayer.add(drawnGraphicRef.current);
      }, 0);
    }
  }, [state, pointGraphicsLayer, drawnGraphicRef]);

  // Clean up when map mode is changed externally
  useEffect(() => {
    if (
      [
        mapAdditionalModes.identify,
        mapAdditionalModes.measure,
        mapAdditionalModes.edit,
        undefined,
      ].includes(mapMode as any)
    ) {
      cleanUp();
    }
  }, [mapMode, cleanUp]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanUp();
    };
    // eslint-disable-next-line
  }, []);

  // Remember last map mode
  useEffect(() => {
    if (mapModeRef.current !== mapMode) {
      mapModeRef.current = mapMode;
    }
  }, [mapMode, mapModeRef]);

  // Clear map mode if needed on unmount
  useEffect(() => {
    return () => {
      if (
        Object.keys(featureTypes).includes(mapModeRef.current as featureTypes)
      ) {
        setMapMode(undefined);
      }
    };
  }, [mapModeRef, setMapMode]);

  const activateDrawOption = (option: featureTypes) => {
    if (mapMode === option) return;
    setMapMode(option);
    setMapDialogProps(initialMapDialogProps);
    if (drawnGraphicRef.current) {
      pointGraphicsLayer.remove(drawnGraphicRef.current);
      drawnGraphicRef.current = undefined;
    }
    if (mapScale <= mapConfig.minDrawingScale) {
      sketchVm.create(drawOptionToSketchTool[option]);
    }
  };

  const instructions =
    nvrToolMode === nvrTools.removal
      ? removalInstructionsText
      : offsetInstructionsText;

  return (
    <Container>
      <InstructionsContainer>
        <TextButtonWithArrow onClick={onHelpClick}>
          {instructions}
        </TextButtonWithArrow>
      </InstructionsContainer>
      <DrawOptionsContainer>
        <DrawOption
          optionId={featureTypes.patch}
          name="Patch (polygon)"
          icon="polygon"
          active={mapMode === featureTypes.patch}
          onActivate={() => activateDrawOption(featureTypes.patch)}
        />
        <DrawOption
          optionId={featureTypes.tree}
          name="Tree (point)"
          icon="point"
          active={mapMode === featureTypes.tree}
          onActivate={() => activateDrawOption(featureTypes.tree)}
        />
        {nvrToolMode === nvrTools.offset && (
          <>
            <Separator />
            <DrawOption
              optionId={featureTypes.revegetationArea}
              name="Revegetation area (polygon)"
              icon="polygon"
              active={mapMode === featureTypes.revegetationArea}
              onActivate={() =>
                activateDrawOption(featureTypes.revegetationArea)
              }
            />
            {showRevegetationWarning && (
              <CalloutContainer>
                <Callout
                  type={"info"}
                  showDismissButton
                  onDismiss={() => setShowRevegetationWarning(false)}
                >
                  <p style={{ marginTop: 0 }}>
                    Revegetation can only be undertaken in woody vegetation
                    types and the areas must conform to the shape requirements.
                  </p>
                  <TextButtonWithArrow onClick={onRevegetationInfoClick}>
                    Read more
                  </TextButtonWithArrow>
                </Callout>
              </CalloutContainer>
            )}
          </>
        )}
      </DrawOptionsContainer>
    </Container>
  );
}

export default DrawWidget;
